import React from "react";
import StepModal from "modules/swiftboard/components/ui/StepModal";
import Skeleton from "components/ui/Skeleton";
import { useAlert } from "context/AlertContext";
import useTemplateWizard from "./useTemplateWizard";
import StepBasicInfo from "./steps/StepBasicInfo";
import StepContentMethod from "./steps/StepContentMethod";
import StepContentEditor from "./steps/StepContentEditor";
import StepTestSend from "./steps/StepTestSend";

const TemplateWizardModal = ({ isOpen, onClose, id, mailingFor = "NOTIFICATIONS", duplicateFrom = null, onSuccess }) => {
  const { showAlert } = useAlert();
  const wizard = useTemplateWizard({ isOpen, id, mailingFor, duplicateFrom });

  if (!isOpen) return null;

  const handleGenerate = async () => {
    try {
      await wizard.generateWithAI();
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "AI generation failed." });
    }
  };

  const handleTestSuccess = () => {
    showAlert({ type: "success", message: "Test email sent successfully." });
  };

  const handleTestError = (err) => {
    showAlert({ type: "error", message: err.response?.data?.message || "Failed to send test email." });
  };

  const handleFinish = async () => {
    try {
      await wizard.finish();
      showAlert({
        type: "success",
        message: `Email template ${wizard.isEditing ? "updated" : "created"} successfully.`,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to save template." });
    }
  };

  const renderStep = () => {
    switch (wizard.STEPS[wizard.step].key) {
      case "basic":
        return (
          <StepBasicInfo
            formData={wizard.formData}
            setField={wizard.setField}
            errors={wizard.errors}
            isEditing={wizard.isEditing}
            lockMailingFor
          />
        );
      case "method":
        return (
          <StepContentMethod
            contentMethod={wizard.contentMethod}
            setContentMethod={wizard.setContentMethod}
            aiPrompt={wizard.aiPrompt}
            setAiPrompt={wizard.setAiPrompt}
            aiGenerating={wizard.aiGenerating}
            aiGenerated={wizard.aiGenerated}
            generateWithAI={handleGenerate}
          />
        );
      case "content":
        return (
          <StepContentEditor
            formData={wizard.formData}
            setField={wizard.setField}
            errors={wizard.errors}
            placeholders={wizard.placeholders}
            insertFields={wizard.insertFields}
            contentMethod={wizard.contentMethod}
            generateWithAI={handleGenerate}
            aiGenerating={wizard.aiGenerating}
          />
        );
      case "test":
        return (
          <StepTestSend
            placeholders={wizard.placeholders}
            dataPoints={wizard.dataPoints}
            setDataPoints={wizard.setDataPoints}
            testEmail={wizard.testEmail}
            setTestEmail={wizard.setTestEmail}
            testing={wizard.testing}
            sendTest={wizard.sendTest}
            isVerifiedThisSession={wizard.isVerifiedThisSession}
            onTestSuccess={handleTestSuccess}
            onTestError={handleTestError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StepModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      title={wizard.isEditing ? "Edit Email Template" : duplicateFrom ? "Duplicate Email Template" : "Create Email Template"}
      steps={wizard.STEPS}
      currentStep={wizard.step}
      canGoNext={wizard.canGoNext}
      canFinish={wizard.isVerifiedThisSession}
      onNext={wizard.goNext}
      onBack={wizard.goBack}
      onFinish={handleFinish}
      finishLabel={wizard.isEditing ? "Save Changes" : "Create Template"}
      isSubmitting={wizard.submitting}
    >
      {wizard.loading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        renderStep()
      )}
    </StepModal>
  );
};

export default TemplateWizardModal;
